import React from 'react';
import { Plane, Building2, CheckCircle2, Clock, Zap, Shield } from 'lucide-react';
import { Button } from './ui/button';

export const Hero = () => {
  const scrollToBooking = () => {
    const element = document.getElementById('booking');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Animated background with clouds */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-orange-50">
        <div className="absolute top-20 left-10 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 right-10 w-64 h-64 bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge with animation */}
          <div className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-full text-sm font-medium mb-6 shadow-lg animate-fade-in-down">
            <Zap size={16} className="mr-2 animate-pulse" />
            Trusted by 5000+ travelers • 98% success rate
          </div>

          {/* Heading with gradient text */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight animate-fade-in">
            Get a verifiable{' '}
            <span className="bg-gradient-to-r from-blue-600 via-blue-500 to-orange-500 bg-clip-text text-transparent">
              flight reservation
            </span>
            <br />
            in 2 minutes
          </h1>

          {/* Subheading */}
          <p className="text-xl text-gray-700 mb-10 max-w-3xl mx-auto animate-fade-in animation-delay-200">
            Perfect for visa applications, digital nomads & flexible travelers. Real PNR codes verified on airline websites.
          </p>

          {/* CTA Button with glow effect */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-4 animate-fade-in animation-delay-400">
            <Button
              onClick={scrollToBooking}
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-10 py-6 text-lg font-bold rounded-xl shadow-2xl hover:shadow-blue-500/50 transition-all transform hover:scale-105"
            >
              Book Now - Starting ₹999
            </Button>
          </div>
          <p className="text-sm text-gray-600 mb-16 animate-fade-in animation-delay-600">
            ⚡ Delivered within 30 minutes • 💯 100% Legal & Embassy Approved
          </p>

          {/* Features Grid with enhanced colors */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            <div className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 border-t-4 border-blue-500 animate-fade-in-up">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-lg group-hover:scale-110 transition-transform">
                <Zap className="text-white" size={28} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-lg">2 Min Delivery</h3>
              <p className="text-sm text-gray-600">Get your reservation via email in just 2 minutes. No delays!</p>
            </div>

            <div className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 border-t-4 border-orange-500 animate-fade-in-up animation-delay-200">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-lg group-hover:scale-110 transition-transform">
                <CheckCircle2 className="text-white" size={28} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-lg">Real & Verifiable</h3>
              <p className="text-sm text-gray-600">Genuine PNR codes that can be verified on airline websites</p>
            </div>

            <div className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 border-t-4 border-green-500 animate-fade-in-up animation-delay-400">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-lg group-hover:scale-110 transition-transform">
                <Shield className="text-white" size={28} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-lg">100% Legal</h3>
              <p className="text-sm text-gray-600">Accepted by embassies worldwide for visa applications</p>
            </div>

            <div className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 border-t-4 border-purple-500 animate-fade-in-up animation-delay-600">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-lg group-hover:scale-110 transition-transform">
                <Building2 className="text-white" size={28} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 text-lg">Hotel Vouchers</h3>
              <p className="text-sm text-gray-600">Complete packages with hotel reservations included</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
